<template>
  <div class="content container-fluid page-bg nopadding">
    <div class="loginbox" v-if="selectedUserType">
      <!-- Smart One-Click Login Card View -->
      <div
        v-if="showSmartLoginCard && smartUser"
        class="smart-login-container p-4"
      >
        <div class="d-flex justify-content-between align-items-center mb-3">
          <span
            class="badge badge-pill badge-primary-custom px-3 py-1 text-uppercase font-weight-bold"
          >
            {{ smartUser.userType }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-link text-muted p-0"
            title="Remove saved account"
            @click="removeSmartAccount"
          >
            <i class="fa fa-times-circle fa-lg"></i>
          </button>
        </div>

        <div class="text-center my-3">
          <div class="avatar-circle mx-auto mb-3">
            <img
              v-if="profilePicUrl && !imageLoadError"
              :src="profilePicUrl"
              class="avatar-img"
              alt="profile"
              @error="imageLoadError = true"
            />
            <span v-else class="avatar-initials">{{ userInitials }}</span>
          </div>
          <h4 class="font-weight-bold mb-1 user-name-text">
            {{ smartUser.name || smartUser.email }}
          </h4>
          <p class="text-muted small mb-0">{{ smartUser.email }}</p>
        </div>

        <div class="text-center mt-4">
          <button
            class="btn btn-cp btn-block rounded-pill py-2 font-weight-bold d-flex justify-content-center align-items-center"
            :disabled="loadingSmartLogin"
            @click="performSmartLogin"
          >
            <span
              v-if="loadingSmartLogin"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
            ></span>
            <i v-else class="fa fa-sign-in mr-2"></i>
            Login as {{ firstNameOnly }}
          </button>

          <button
            type="button"
            class="btn btn-link text-secondary text-decoration-none mt-3 small font-weight-bold"
            @click="useAnotherAccount"
          >
            <i class="fa fa-user-plus mr-1"></i> Sign in with a different
            account
          </button>
        </div>
      </div>

      <!-- Standard Tabbed Login View -->
      <div
        v-else
        class="d-flex flex-column flex-grow-1 justify-content-between p-2"
        style="height: 100%"
      >
        <div class="d-flex flex-column flex-grow-1" style="height: 100%">
          <div
            v-if="smartUser"
            class="smart-login-banner px-3 pt-2 text-center"
          >
            <a
              href="#"
              class="smart-login-badge text-decoration-none d-inline-flex align-items-center"
              @click.prevent="showSmartLoginCard = true"
            >
              <i class="fa fa-user-circle mr-2"></i>
              <span
                >Back to Smart Login (<strong>{{
                  smartUser.name || smartUser.email
                }}</strong
                >)</span
              >
            </a>
          </div>

          <b-tabs
            justified
            v-model="activeTab"
            class="d-flex flex-column flex-grow-1"
            style="height: 100%"
            content-class="d-flex flex-column flex-grow-1 justify-content-center"
          >
            <b-tab
              title="Employee"
              v-model="selectedUserType"
              @click="changeUserType('employee')"
            >
              <div
                v-if="selectedUserType === 'employee'"
                class="employee-login"
              >
                <div class="logo-div">
                  <img
                    class="logoSize"
                    src="../../assets/images/logo.svg"
                    alt="logo"
                  />
                </div>
                <div class="text-center w-100">
                  <h5 class="text-center font-weight-bold" @click="goToLogin">
                    Sign In With
                    <a
                      ><img src="../../assets/images/mail.png" alt="mailLogo"
                    /></a>
                  </h5>
                </div>
              </div>
            </b-tab>
            <b-tab title="Recruiter" @click="changeUserType('recruiter')">
              <div v-if="selectedUserType === 'recruiter'">
                <recruiter-login />
              </div>
            </b-tab>
            <b-tab
              title="Applicant"
              @click="changeUserType('applicant')"
              class="tab-content"
            >
              <div v-if="selectedUserType === 'applicant'">
                <applicant-login />
              </div>
            </b-tab>
          </b-tabs>
        </div>

        <div class="font-weight-bold mx-3 mb-2">
          <h3 class="p-0 my-2"></h3>
          <router-link :to="'/'">
            <i class="fa fa-long-arrow-left" aria-hidden="true"></i>
            <span style="padding: 4px">Homepage</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from "../../config";
import applicantMasterStore from "../ApplicantMaster/_store";
import recruiterLogin from "../Recruiter/_components/recruiter-login.vue";
import applicantLogin from "../ApplicantMaster/_components/applicant-login.vue";
import recruiterApi from "../Recruiter/_api/index";

const userTypes = ["employee", "recruiter", "applicant"];
const tabIndex = {
  employee: 0,
  recruiter: 1,
  applicant: 2,
};

export default {
  components: { recruiterLogin, applicantLogin },
  data() {
    return {
      config,
      slotId: this.$route.query.slot,
      userTypes: ["employee", "recruiter", "applicant"],
      selectedUserType: userTypes.includes(this.$route.query.as)
        ? this.$route.query.as
        : "employee",
      activeTab: userTypes.includes(this.$route.query.as)
        ? tabIndex[this.$route.query.as]
        : tabIndex["employee"],
      smartUser: null,
      showSmartLoginCard: false,
      loadingSmartLogin: false,
      imageLoadError: false,
    };
  },
  computed: {
    profilePicUrl() {
      if (!this.smartUser) return "";
      const pic = this.smartUser.profilePic || this.smartUser.profile_pic;
      if (
        pic &&
        (pic.startsWith("http://") ||
          pic.startsWith("https://") ||
          pic.startsWith("data:image/"))
      ) {
        return pic;
      }
      if (this.smartUser.email) {
        return `https://unavatar.io/google/${encodeURIComponent(
          this.smartUser.email
        )}`;
      }
      return "";
    },
    userInitials() {
      if (!this.smartUser) return "?";
      const nameStr =
        (this.smartUser.name && this.smartUser.name.trim()) ||
        (this.smartUser.email && this.smartUser.email.trim());
      if (!nameStr) return "?";

      if (nameStr.includes("@")) {
        const emailUser = nameStr.split("@")[0];
        const parts = emailUser.split(/[._-]/).filter(Boolean);
        if (parts.length >= 2) {
          return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return parts[0].substring(0, 2).toUpperCase();
      }

      const parts = nameStr.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    },
    firstNameOnly() {
      if (!this.smartUser) return "";
      const nameStr =
        (this.smartUser.name && this.smartUser.name.trim()) ||
        (this.smartUser.email && this.smartUser.email.trim());
      if (!nameStr) return "";
      if (nameStr.includes("@")) {
        const emailUser = nameStr.split("@")[0];
        const parts = emailUser.split(/[._-]/).filter(Boolean);
        return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      }
      const parts = nameStr.split(/\s+/).filter(Boolean);
      return parts[0];
    },
  },
  watch: {
    selectedUserType: {
      handler: function (value) {
        if (value && value !== this.$route.query.as) {
          this.$router.replace({
            name: "login",
            query: { as: value },
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    goToLogin(loginHint) {
      let url = this.config.ROOT_API + "/auth/google";
      if (typeof loginHint === "string" && loginHint.length > 0) {
        url += "?login_hint=" + encodeURIComponent(loginHint);
      }
      window.location = url;
    },
    changeUserType(user) {
      this.selectedUserType = user;
      this.activeTab = tabIndex[user];
    },
    async performSmartLogin() {
      if (!this.smartUser) return;
      this.loadingSmartLogin = true;

      if (this.smartUser.userType === "recruiter") {
        try {
          const response = await recruiterApi.loginUser({
            email: this.smartUser.email,
            accountPassword: this.smartUser.password,
          });
          if (response.data.token) {
            this.$cookie.set("RECRUITER_ACCESS_TOKEN", response.data.token);
            localStorage.setItem("RECRUITER_ACCESS_TOKEN", response.data.token);
            this.$store.dispatch(
              "openSnackbar",
              {
                type: "success",
                message: "Logged in successfully!",
              },
              { root: true }
            );
            this.$router.push("/recruiter/dashboard");
          } else {
            this.$store.dispatch(
              "openSnackbar",
              {
                type: "danger",
                message: response.data.message || "Login failed",
              },
              { root: true }
            );
            this.removeSmartAccount();
          }
        } catch (error) {
          this.$store.dispatch(
            "openSnackbar",
            {
              type: "danger",
              message:
                error.response && error.response.data
                  ? error.response.data.message
                  : "Login failed. Please enter credentials.",
            },
            { root: true }
          );
          this.removeSmartAccount();
        } finally {
          this.loadingSmartLogin = false;
        }
      } else if (this.smartUser.userType === "applicant") {
        try {
          await this.$store.dispatch("$_applicant_master/applicantLogin", {
            email: this.smartUser.email,
            password: this.smartUser.password,
          });
        } catch (error) {
          this.removeSmartAccount();
        } finally {
          this.loadingSmartLogin = false;
        }
      } else if (this.smartUser.userType === "employee") {
        this.goToLogin(this.smartUser.email);
      }
    },
    useAnotherAccount() {
      this.showSmartLoginCard = false;
    },
    removeSmartAccount() {
      this.$cookie.delete("smart_login_credentials");
      this.smartUser = null;
      this.showSmartLoginCard = false;
    },
    checkSmartLoginCookie() {
      const cookieData = this.$cookie.get("smart_login_credentials");
      if (cookieData) {
        try {
          const parsed =
            typeof cookieData === "string"
              ? JSON.parse(cookieData)
              : cookieData;
          if (parsed && parsed.email) {
            this.smartUser = parsed;
            this.showSmartLoginCard = true;
          }
        } catch (e) {
          console.error("Failed to parse smart login cookie", e);
        }
      }
    },
  },
  created() {
    const STORE_KEY = "$_applicant_master";
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, applicantMasterStore);
    }
    this.checkSmartLoginCookie();
  },
};
</script>

<style scoped>
.smart-login-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
}

.avatar-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8f1452 0%, #b8286f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(143, 20, 82, 0.25);
}

.avatar-initials {
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.badge-primary-custom {
  background-color: #8f1452;
  color: white;
  letter-spacing: 0.5px;
}

.text-primary-custom {
  color: #8f1452;
}

.user-name-text {
  color: #2c3e50;
  font-size: 1.25rem;
}

.btn-cp {
  background-color: #8f1452;
  color: white;
  border: none;
  transition: all 0.3s ease;
}

.btn-cp:hover:not(:disabled) {
  background-color: #721042;
  color: white;
  box-shadow: 0 4px 10px rgba(143, 20, 82, 0.3);
}

.btn-cp:disabled {
  opacity: 0.7;
}

.add-cursor {
  cursor: pointer;
}

.mt-30 {
  margin-top: 30px;
}
.mb-30 {
  margin-bottom: 30px;
}
.logo-div {
  padding: 15px;
  padding-bottom: 20px;
  display: flex;
  justify-content: space-around;
}

.smart-login-banner {
  position: relative;
  z-index: 5;
  margin-bottom: 12px;
}

.smart-login-badge {
  padding: 6px 16px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  color: #8f1452;
  font-size: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  transition: all 0.25s ease;
}

.employee-login {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 100%;
}

.smart-login-badge:hover {
  background-color: #8f1452;
  color: #ffffff;
  border-color: #8f1452;
  box-shadow: 0 4px 10px rgba(143, 20, 82, 0.25);
}

.smart-login-badge:hover strong {
  color: #ffffff !important;
}

.loginbox {
  margin: auto;
  max-width: 420px;
  width: 100%;
  min-height: 450px;
  height: auto;
  display: flex;
  flex-direction: column;
  background-color: rgb(255, 255, 255);
  box-shadow: 1px 2px 8px #5b5558;
  border-radius: 25px;
}
.loginbox h5 {
  letter-spacing: 0.5px;
  display: block;
  border-radius: 30px;
  padding: 8px 20px;
  border: 1px solid #8f1452;
  max-width: 220px;
  margin: 20px auto;
}

.loginbox h5:hover {
  background-color: #8f1452;
  color: white;
  cursor: pointer;
}

.loginbox h5 img {
  width: 30px;
  height: 30px;
}

.loginbox h5 a {
  padding-left: 20px;
}

.page-bg {
  /* height: 100vh; */
  background-repeat: no-repeat;
  background-size: cover;
}
.home {
  position: absolute;
  width: 100%;
  text-align: center;
  bottom: 15px;
}

@media (max-width: 768px) {
  .loginbox {
    width: 90%;
    height: 474px;
  }

  .home {
    position: fixed;
    width: 100%;
    text-align: center;
    bottom: 10px;
  }
}
h3 {
  text-align: center;
  border-bottom: 1px solid #efe6e6;
  padding-bottom: 15px;
}
.content {
  margin: auto !important;
}
::v-deep #maincontent {
  margin-top: 0px !important;
}
::v-deep .tabs {
  display: flex !important;
  flex-direction: column !important;
  flex-grow: 1 !important;
  height: 100% !important;
}
::v-deep .tab-content > .tab-pane {
  display: none;
}
::v-deep .tab-content > .tab-pane.active {
  display: flex !important;
  flex-direction: column !important;
  flex-grow: 1 !important;
  justify-content: center !important;
  align-items: center !important;
  height: 100% !important;
}
</style>
